import { useMemo, useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { ExternalUrlGroupsTranslation } from '../../constants';
import { ExternalUrlGroupsItemValidation, ExternalUrlsValidation, AccessPolicyValidation } from '../types-store';
import { ExternalUrlGroupsSectionType } from './types';
import { ExternalUrlGroupsActionType } from 'app/area/external-url-groups/form/types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getExternalUrlGroupsRoute = (
  actionType: ExternalUrlGroupsActionType,
  id: number,
  section?: ExternalUrlGroupsSectionType
) => actionType && {
  [ExternalUrlGroupsActionType.add]: routeConstants.externalUrlGroupsAddFor(section),
  [ExternalUrlGroupsActionType.edit]: routeConstants.externalUrlGroupsEditFor(id, section)
}[actionType];

export const useExternalUrlGroupsConfiguration = (
  actionType: ExternalUrlGroupsActionType,
  id: number,
  validationErrors: ExternalUrlGroupsItemValidation,
  accessPolicyValidation: AccessPolicyValidation,
  externalUrlsValidation: ExternalUrlsValidation
): Configuration<ExternalUrlGroupsSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement | null>(null);
  const translationsSectionRef = useRef<HTMLDivElement | null>(null);
  const accessPolicySectionRef = useRef<HTMLDivElement | null>(null);
  const externalUrlSectionRef = useRef<HTMLDivElement | null>(null);

  return useMemo(() => {

    const getSectionRoute = (section: ExternalUrlGroupsSectionType) => getExternalUrlGroupsRoute(actionType, id, section);
    const renderLanguageOnTop = !isDefinedIncludingZero(id);
    const languageSection = {
      section: ExternalUrlGroupsSectionType.Translations,
      visible: true,
      url: getSectionRoute(ExternalUrlGroupsSectionType.Translations),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: ExternalUrlGroupsTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: isValid([validationErrors.language], ValidationLevel.error)
    };

    return [
      ...(renderLanguageOnTop ? [languageSection] : []),
      {
        section: ExternalUrlGroupsSectionType.Details,
        visible: true,
        iconUrl: '/i/case-sections/default.svg',
        url: getSectionRoute(ExternalUrlGroupsSectionType.Details),
        label: (translation: ExternalUrlGroupsTranslation) => translation.details,
        ref: detailsSectionRef,
        isValid: isValid([validationErrors.disabled], ValidationLevel.error)
      },
      {
        section: ExternalUrlGroupsSectionType.accessPolicy,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(ExternalUrlGroupsSectionType.accessPolicy),
        label: (translation: ExternalUrlGroupsTranslation) => translation.access_policy_title,
        ref: accessPolicySectionRef,
        isValid: !Object.keys(accessPolicyValidation ?? {}).length
          && !validationErrors?.caseType?.messages?.length
          && !validationErrors?.operation?.messages?.length
      },
      {
        section: ExternalUrlGroupsSectionType.ExternalUrls,
        visible: true,
        iconUrl: '/i/case-sections/connected-cases.svg',
        url: getSectionRoute(ExternalUrlGroupsSectionType.ExternalUrls),
        label: (translation: ExternalUrlGroupsTranslation) => translation.external_urls_title,
        ref: externalUrlSectionRef,
        isValid: !Object.keys(externalUrlsValidation ?? {}).length
      },
      ...(!renderLanguageOnTop ? [languageSection] : [])
    ];
  }, [id, actionType, detailsSectionRef, translationsSectionRef, accessPolicyValidation, externalUrlsValidation, validationErrors]);
};
